import {
  useCallback,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";

/** A point in client (viewport) pixels. */
export interface Point {
  x: number;
  y: number;
}

/** Snap step for the rotate gesture, in degrees (see [[directional-pins]]). */
const SNAP_STEP_DEGREES = 15;

/**
 * Half-width of the magnetic basin around each of the 8 cardinal points
 * (0/45/90/.../315), in degrees. A raw angle within this distance of a cardinal
 * snaps to the cardinal instead of the nearest 15° step — a gentle pull so
 * "due east" lands square rather than one tick off. Wider than a half-step
 * (7.5°) so the cardinal genuinely wins its neighbourhood; kept below 15° so it
 * never swallows the adjacent non-cardinal steps.
 */
const CARDINAL_PULL_DEGREES = 10;

/** The 8 cardinal/intercardinal bearings the drag is magnetically pulled to. */
const CARDINALS = [0, 45, 90, 135, 180, 225, 270, 315] as const;

/** Wrap any angle into the plan convention's `[0, 360)` range. */
function normalizeDegrees(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Bearing from a pin centre to a pointer, in the plan convention: degrees,
 * `[0, 360)`, clockwise, **0° = up (toward drawing-top)**, 90° = right, 180° =
 * down, 270° = left.
 *
 * Screen y grows downward, so we negate `dy`: `atan2(dx, -dy)` measures the
 * angle clockwise from straight up. (Contrast the textbook `atan2(dy, dx)`,
 * which is counter-clockwise from the +x axis.)
 */
export function computeBearing(center: Point, pointer: Point): number {
  const dx = pointer.x - center.x;
  const dy = pointer.y - center.y;
  const deg = Math.atan2(dx, -dy) * (180 / Math.PI);
  return normalizeDegrees(deg);
}

/**
 * Snaps a raw bearing to a 15° increment, with a magnetic pull toward the 8
 * cardinal points: if the raw angle is within {@link CARDINAL_PULL_DEGREES} of
 * a cardinal, it snaps to that cardinal; otherwise to the nearest 15° step.
 * Cardinals are themselves multiples of 15, so the pull only *widens* their
 * basin — it never produces an off-grid value. Result is in `[0, 360)`.
 */
export function snapBearing(rawBearing: number): number {
  const bearing = normalizeDegrees(rawBearing);

  // Cardinal pull: nearest cardinal wins if we're inside its basin. Compare on
  // the circle so 359° pulls to 0°, not past it.
  let nearestCardinal: number = CARDINALS[0];
  let nearestCardinalDelta = Infinity;
  for (const cardinal of CARDINALS) {
    const delta = Math.abs(((bearing - cardinal + 540) % 360) - 180);
    if (delta < nearestCardinalDelta) {
      nearestCardinalDelta = delta;
      nearestCardinal = cardinal;
    }
  }
  if (nearestCardinalDelta <= CARDINAL_PULL_DEGREES) {
    return nearestCardinal;
  }

  // Otherwise snap to the nearest 15° step. `% 360` collapses a 360 result
  // (e.g. snapping 352.5°) back to 0.
  const stepped = Math.round(bearing / SNAP_STEP_DEGREES) * SNAP_STEP_DEGREES;
  return normalizeDegrees(stepped);
}

export interface UseDragToRotateOptions {
  /**
   * Reads the pin centre in client (viewport) px at gesture time. Provide
   * either this or {@link anchorRef}; `getCenter` wins if both are given. Read
   * lazily (on each pointer move) so a panned/zoomed plan stays correct.
   */
  getCenter?: () => Point;
  /**
   * Element whose bounding-box centre is the pin centre — a convenience when
   * the pin has a DOM node. Ignored if {@link getCenter} is supplied.
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Fired with the snapped bearing on every move while dragging. */
  onChange: (bearing: number) => void;
  /** Fired once when the drag ends (pointer up), with the final bearing. */
  onCommit?: (bearing: number) => void;
  /** Skip wiring the gesture (handlers become no-ops). */
  enabled?: boolean;
}

/** Props to spread onto the draggable rotate handle element. */
export interface DragToRotateHandleProps {
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerMove: (e: ReactPointerEvent) => void;
  onPointerUp: (e: ReactPointerEvent) => void;
  onPointerCancel: (e: ReactPointerEvent) => void;
  style: React.CSSProperties;
}

/**
 * A reusable pointer-drag-to-rotate gesture for the directional-pin rotate
 * handle. Returns `handleProps` to spread on the handle element; dragging it
 * sweeps the cone, reporting the snapped bearing via `onChange` (live) and
 * `onCommit` (on release).
 *
 * Behaviour, per [[directional-pins]]:
 *  - Bearing is the angle from the pin centre to the pointer in the plan
 *    convention (0° = up, clockwise) — see {@link computeBearing}.
 *  - Snaps to 15° with a magnetic pull at the 8 cardinals — see
 *    {@link snapBearing}.
 *  - **Pan-isolated**: pointer events `stopPropagation` (and the down/move
 *    `preventDefault`) so a rotate-drag never pans/zooms the plan or drops a
 *    second pin underneath.
 *  - Only the **primary** pointer drives the gesture; secondary touches are
 *    ignored. `setPointerCapture` keeps tracking when the finger leaves the
 *    handle.
 */
export function useDragToRotate({
  getCenter,
  anchorRef,
  onChange,
  onCommit,
  enabled = true,
}: UseDragToRotateOptions): { handleProps: DragToRotateHandleProps } {
  // Id of the captured pointer while a drag is active; null when idle. A ref
  // (not state) — the gesture is imperative and must not re-render per move.
  const activePointerRef = useRef<number | null>(null);

  const readCenter = useCallback((): Point | null => {
    if (getCenter) return getCenter();
    const el = anchorRef?.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }, [getCenter, anchorRef]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      // Primary pointer only — ignore secondary touches / non-primary buttons.
      if (!e.isPrimary) return;
      // Isolate from the plan: never let this become a pan or a pin-drop.
      e.stopPropagation();
      e.preventDefault();
      const center = readCenter();
      if (!center) return;
      activePointerRef.current = e.pointerId;
      // Track the finger even when it leaves the small handle.
      e.currentTarget.setPointerCapture(e.pointerId);
      onChange(snapBearing(computeBearing(center, { x: e.clientX, y: e.clientY })));
    },
    [readCenter, onChange],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (activePointerRef.current !== e.pointerId) return;
      e.stopPropagation();
      e.preventDefault();
      const center = readCenter();
      if (!center) return;
      onChange(snapBearing(computeBearing(center, { x: e.clientX, y: e.clientY })));
    },
    [readCenter, onChange],
  );

  const endDrag = useCallback(
    (e: ReactPointerEvent) => {
      if (activePointerRef.current !== e.pointerId) return;
      e.stopPropagation();
      activePointerRef.current = null;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      const center = readCenter();
      if (center) {
        onCommit?.(
          snapBearing(computeBearing(center, { x: e.clientX, y: e.clientY })),
        );
      }
    },
    [readCenter, onCommit],
  );

  const noop = useCallback((e: ReactPointerEvent) => {
    // Even disabled, swallow the event so it can't reach the plan beneath.
    e.stopPropagation();
  }, []);

  if (!enabled) {
    return {
      handleProps: {
        onPointerDown: noop,
        onPointerMove: noop,
        onPointerUp: noop,
        onPointerCancel: noop,
        style: { touchAction: "none" },
      },
    };
  }

  return {
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      // `touch-action: none` stops the browser hijacking the drag for
      // scroll/zoom on touch — the gesture owns the pointer outright.
      style: { touchAction: "none" },
    },
  };
}
